/**
 * Tutorial Context Module
 *
 * Provides centralized tutorial state management for the application.
 * This module implements a React Context-based tutorial system that guides users
 * through the application's features with an interactive step-by-step walkthrough.
 *
 * @module contexts/TutorialContext
 */
import { createContext, useState, useEffect, ReactNode, useMemo, useContext } from 'react';
import { getTutorialStepByIndex } from '../config/tutorialSteps';
import { measureTutorialActivation } from '../utils/performanceMonitor';
/**
 * Tutorial state interface
 *
 * @interface TutorialState
 * @property {boolean} isActive - Whether tutorial is currently running
 * @property {number} currentStep - Current step index (0-based)
 * @property {number} totalSteps - Total number of tutorial steps
 * @property {boolean} isCompleted - Whether user has completed tutorial before
 * @property {boolean} isTransitioning - Whether currently transitioning between steps
 * @property {boolean} waitingForAction - Whether waiting for user action to proceed
 */
interface TutorialState {
    isActive: boolean;
    currentStep: number;
    totalSteps: number;
    isCompleted: boolean;
    isTransitioning: boolean;
    waitingForAction: boolean;
}
/**
 * Tutorial context value interface
 *
 * @interface TutorialContextValue
 * @property {boolean} isActive - Whether tutorial is currently running
 * @property {number} currentStep - Current step index (0-based)
 * @property {number} totalSteps - Total number of tutorial steps
 * @property {boolean} isCompleted - Whether user has completed tutorial before
 * @property {boolean} isTransitioning - Whether currently transitioning between steps
 * @property {boolean} waitingForAction - Whether waiting for user action to proceed
 * @property {() => void} startTutorial - Start the tutorial from the beginning
 * @property {() => void} exitTutorial - Exit the tutorial
 * @property {() => void} nextStep - Advance to the next step
 * @property {() => void} previousStep - Go back to the previous step
 * @property {() => void} completeStep - Mark tutorial as complete (called on final step)
 * @property {() => void} resetTutorial - Reset tutorial completion status
 * @property {() => void} completeAction - Mark the current action as complete (for waitForAction steps)
 */
export interface TutorialContextValue extends TutorialState {
    startTutorial: () => void;
    exitTutorial: () => void;
    nextStep: () => void;
    previousStep: () => void;
    completeStep: () => void;
    resetTutorial: () => void;
    completeAction: () => void;
}
/**
 * Tutorial storage data interface
 *
 * @interface TutorialStorageData
 * @property {boolean} isCompleted - Whether tutorial has been completed
 * @property {string} [completedAt] - ISO timestamp of completion
 * @property {string} version - Tutorial version for future updates
 */
interface TutorialStorageData {
    isCompleted: boolean;
    completedAt?: string;
    version: string;
}
/**
 * Tutorial Context
 *
 * React Context for sharing tutorial state across the application.
 * Provides access to tutorial state and control functions.
 *
 * @context
 * @type {React.Context<TutorialContextValue | undefined>}
 */
export const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);
/**
 * TutorialProvider Props
 *
 * @interface TutorialProviderProps
 * @property {ReactNode} children - Child components to be wrapped by the tutorial provider
 */
interface TutorialProviderProps {
    children: ReactNode;
}
/**
 * LocalStorage key for tutorial state
 */
const TUTORIAL_STORAGE_KEY = '8bitcomposer_tutorial_state';
/**
 * Current tutorial version
 */
const TUTORIAL_VERSION = '2.0.0';
/**
 * Total number of tutorial steps
 * This will match the number of steps defined in tutorialSteps.ts
 */
const TOTAL_TUTORIAL_STEPS = 15;
/**
 * Load tutorial completion status from localStorage
 *
 * Attempts to read the user's tutorial completion status from browser localStorage.
 * Includes error handling and validation for corrupted or invalid data.
 *
 * @function loadTutorialState
 * @returns {boolean} True if tutorial has been completed, false otherwise
 */
const loadTutorialState = (): boolean => {
    try {
        const saved = localStorage.getItem(TUTORIAL_STORAGE_KEY);
        if (saved) {
            const data: TutorialStorageData = JSON.parse(saved);
            // Validate data structure and types
            if (typeof data.isCompleted === 'boolean' &&
                typeof data.version === 'string' &&
                data.version.length > 0) {
                // Check if the saved version matches the current version
                if (data.version !== TUTORIAL_VERSION) {
                    return false;
                }
                // Additional validation: check if completedAt is a valid ISO date if present
                if (data.completedAt) {
                    const date = new Date(data.completedAt);
                    if (isNaN(date.getTime())) {
                        console.warn('Invalid completedAt date in tutorial state, resetting');
                        return false;
                    }
                }
                return data.isCompleted;
            }
            else {
                console.warn('Invalid tutorial state structure, resetting');
            }
        }
    }
    catch (error) {
        // Handle localStorage errors or JSON parse errors
        console.warn('Failed to load tutorial state from localStorage:', error);
        // Attempt to clear corrupted data
        try {
            localStorage.removeItem(TUTORIAL_STORAGE_KEY);
        }
        catch (clearError) {
            console.warn('Failed to clear corrupted tutorial state:', clearError);
        }
    }
    return false;
};
/**
 * Save tutorial completion status to localStorage
 *
 * Persists the user's tutorial completion status to browser localStorage.
 * Includes error handling for localStorage failures.
 *
 * @function saveTutorialState
 * @param {boolean} isCompleted - Whether tutorial has been completed
 */
const saveTutorialState = (isCompleted: boolean): void => {
    try {
        const data: TutorialStorageData = isCompleted
            ? {
                isCompleted,
                completedAt: new Date().toISOString(),
                version: TUTORIAL_VERSION,
            }
            : {
                isCompleted,
                version: TUTORIAL_VERSION,
            };
        localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(data));
    }
    catch (error) {
        // Handle localStorage errors gracefully - app continues to work without persistence
        console.warn('Failed to save tutorial state to localStorage:', error);
    }
};
/**
 * TutorialProvider Component
 *
 * Wraps the application to provide tutorial context to all child components.
 * Manages tutorial state, step navigation, and localStorage persistence.
 *
 * Features:
 * - Loads saved completion status from localStorage on mount
 * - Provides tutorial control functions to all child components
 * - Persists completion status to localStorage
 * - Manages tutorial flow (start, exit, next, previous, complete)
 *
 * @component
 * @param {TutorialProviderProps} props - Component props
 * @param {ReactNode} props.children - Child components to wrap
 */
export function TutorialProvider({ children }: TutorialProviderProps) {
    // Initialize state with completion status from localStorage
    const [state, setState] = useState<TutorialState>(() => ({
        isActive: false,
        currentStep: 0,
        totalSteps: TOTAL_TUTORIAL_STEPS,
        isCompleted: loadTutorialState(),
        isTransitioning: false,
        waitingForAction: false,
    }));
    /**
     * Effect: Save completion status to localStorage when it changes
     */
    useEffect(() => {
        if (state.isCompleted) {
            saveTutorialState(true);
        }
    }, [state.isCompleted]);
    /**
     * Effect: Handle browser back/forward navigation during tutorial
     *
     * Exits tutorial if user navigates away using browser controls.
     * This prevents the tutorial from being stuck in an inconsistent state.
     */
    useEffect(() => {
        if (!state.isActive)
            return;
        const handlePopState = () => {
            // Exit tutorial when user navigates using browser back/forward
            setState(prev => ({
                ...prev,
                isActive: false,
                currentStep: 0,
                isTransitioning: false,
                waitingForAction: false,
            }));
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [state.isActive]);
    /**
     * Effect: Cleanup when tutorial is interrupted or component unmounts
     *
     * Ensures proper cleanup of tutorial state and resources when:
     * - Component unmounts
     * - Tutorial is interrupted
     * - User navigates away
     */
    useEffect(() => {
        return () => {
            // Cleanup: Reset tutorial state on unmount
            if (state.isActive) {
            }
        };
    }, [state.isActive]);
    /**
     * Effect: Handle waitForAction logic when step changes
     *
     * Checks if the current step requires waiting for user action.
     * If so, sets waitingForAction state to true.
     */
    useEffect(() => {
        if (!state.isActive)
            return;
        const currentStepConfig = getTutorialStepByIndex(state.currentStep);
        if (currentStepConfig?.waitForAction) {
            setState(prev => ({
                ...prev,
                waitingForAction: true,
            }));
        }
        // Call onEnter callback if defined
        if (currentStepConfig?.onEnter) {
            currentStepConfig.onEnter();
        }
    }, [state.isActive, state.currentStep]);
    /**
     * Start tutorial
     *
     * Activates the tutorial and resets to the first step.
     * Measures tutorial activation time for performance monitoring.
     *
     * @function startTutorial
     */
    const startTutorial = (): void => {
        // Start performance measurement
        const endMeasurement = measureTutorialActivation();
        setState(prev => ({
            ...prev,
            isActive: true,
            currentStep: 0,
        }));
        // End performance measurement after state update
        // Use setTimeout to measure after React has processed the state update
        setTimeout(() => {
            endMeasurement();
        }, 0);
    };
    /**
     * Exit tutorial
     *
     * Deactivates the tutorial and resets to the first step.
     * Does not mark tutorial as completed.
     * Performs cleanup of tutorial state.
     *
     * @function exitTutorial
     */
    const exitTutorial = (): void => {
        // Call onExit callback for current step if defined
        const currentStepConfig = getTutorialStepByIndex(state.currentStep);
        if (currentStepConfig?.onExit) {
            try {
                currentStepConfig.onExit();
            }
            catch (error) {
                console.warn('Error calling onExit for tutorial step:', error);
            }
        }
        // Reset all tutorial state
        setState(prev => ({
            ...prev,
            isActive: false,
            currentStep: 0,
            isTransitioning: false,
            waitingForAction: false,
        }));
    };
    /**
     * Advance to next step
     *
     * Increments the current step index with transition animation.
     * Does nothing if already on the last step or if waiting for action.
     * Calls onExit callback for current step before advancing.
     *
     * @function nextStep
     */
    const nextStep = (): void => {
        // Call onExit callback for current step if defined
        const currentStepConfig = getTutorialStepByIndex(state.currentStep);
        if (currentStepConfig?.onExit) {
            try {
                currentStepConfig.onExit();
            }
            catch (error) {
                console.warn('Error calling onExit for tutorial step:', error);
            }
        }
        setState(prev => {
            // Don't advance if waiting for action or already on last step
            if (prev.waitingForAction || prev.currentStep >= prev.totalSteps - 1) {
                return prev;
            }
            // Start transition
            return {
                ...prev,
                isTransitioning: true,
            };
        });
        // After fade-out animation (300ms), change step and fade in
        setTimeout(() => {
            setState(prev => {
                if (prev.currentStep < prev.totalSteps - 1) {
                    return {
                        ...prev,
                        currentStep: prev.currentStep + 1,
                        isTransitioning: false,
                        waitingForAction: false, // Reset waiting state for new step
                    };
                }
                return { ...prev, isTransitioning: false };
            });
        }, 300);
    };
    /**
     * Go back to previous step
     *
     * Decrements the current step index with transition animation.
     * Does nothing if already on the first step.
     * Calls onExit callback for current step before going back.
     *
     * @function previousStep
     */
    const previousStep = (): void => {
        // Call onExit callback for current step if defined
        const currentStepConfig = getTutorialStepByIndex(state.currentStep);
        if (currentStepConfig?.onExit) {
            try {
                currentStepConfig.onExit();
            }
            catch (error) {
                console.warn('Error calling onExit for tutorial step:', error);
            }
        }
        setState(prev => {
            // Don't go back if already on first step
            if (prev.currentStep <= 0) {
                return prev;
            }
            // Start transition
            return {
                ...prev,
                isTransitioning: true,
            };
        });
        // After fade-out animation (300ms), change step and fade in
        setTimeout(() => {
            setState(prev => {
                if (prev.currentStep > 0) {
                    return {
                        ...prev,
                        currentStep: prev.currentStep - 1,
                        isTransitioning: false,
                        waitingForAction: false, // Reset waiting state for new step
                    };
                }
                return { ...prev, isTransitioning: false };
            });
        }, 300);
    };
    /**
     * Complete tutorial
     *
     * Marks the tutorial as completed, deactivates it, and persists status.
     * Should be called when user finishes the final step.
     *
     * @function completeStep
     */
    const completeStep = (): void => {
        setState(prev => ({
            ...prev,
            isActive: false,
            currentStep: 0,
            isCompleted: true,
        }));
    };
    /**
     * Reset tutorial
     *
     * Clears the completion status and allows user to take tutorial again.
     * Useful for testing or if user wants to review the tutorial.
     *
     * @function resetTutorial
     */
    const resetTutorial = (): void => {
        setState(prev => ({
            ...prev,
            isCompleted: false,
        }));
        saveTutorialState(false);
    };
    /**
     * Complete action
     *
     * Marks the current action as complete for steps that wait for user action.
     * This allows the tutorial to proceed to the next step.
     *
     * @function completeAction
     */
    const completeAction = (): void => {
        setState(prev => ({
            ...prev,
            waitingForAction: false,
        }));
    };
    /**
     * Memoize context value to prevent unnecessary re-renders
     *
     * Only recreates the context value when state changes.
     */
    const contextValue: TutorialContextValue = useMemo(() => ({
        ...state,
        startTutorial,
        exitTutorial,
        nextStep,
        previousStep,
        completeStep,
        resetTutorial,
        completeAction,
    }), [state]);
    return (<TutorialContext.Provider value={contextValue}>
      {children}
    </TutorialContext.Provider>);
}
/**
 * useTutorial Hook
 *
 * Custom hook for accessing tutorial context.
 * Must be used within a TutorialProvider.
 *
 * @hook
 * @returns {TutorialContextValue} Tutorial context value with state and actions
 * @throws {Error} If used outside of TutorialProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isActive, startTutorial, nextStep } = useTutorial();
 *
 *   return (
 *     <button onClick={startTutorial}>
 *       Start Tutorial
 *     </button>
 *   );
 * }
 * ```
 */
export function useTutorial(): TutorialContextValue {
    const context = useContext(TutorialContext);
    if (!context) {
        throw new Error('useTutorial must be used within a TutorialProvider');
    }
    return context;
}
